import React, { useEffect, useState } from 'react';
import { DatePicker, Select, Button, Table } from 'antd';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Title from '../../components/title';
import withAuth from '../../hoc';
import { getAllReports, cleanEstablishment, getDowndloadReports } from '../../redux/actions/establishment';
import Spinner from '../../components/spinner';
import ChartReport from './chart';
import { setSelectedModuleId } from '../../redux/actions/module';

const dateFormat = 'DD/MM/YYYY HH:mm:ss';
const { RangePicker } = DatePicker;

const rangeOptions = [
  { label: '1 hora', value: '1h' },
  { label: '3 horas', value: '3h' },
  { label: '12 horas', value: '12h' },
  { label: 'Día', value: '24h' },
  { label: 'Semana', value: 'week' },
  { label: 'Mes', value: 'month' },
  { label: 'Trimestre', value: 'quarter' },
  { label: 'Año', value: 'year' },
];

const Reports = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { reports, isLoading, dataDownload } = useSelector((state) => state.establishmentReducer);
  const { selectedModuleIdCard } = useSelector((state) => state.moduleReducer);
  const [isLive, setIsLive] = useState(true);
  const [selectedModuleId, setSelectedModuleIdChart] = useState(null);
  const [selectedRange, setSelectedRange] = useState('24h');
  const [dates, setDates] = useState([
    dayjs().subtract(24, 'hour'),
    dayjs()
  ]);
  const ultimatedTimeSensor =
  reports?.modulos
    ?.find(modulo =>
      modulo?.perfilcultivo?.parametros?.some(p => p.sensor?.valores?.length > 0)
    )
    ?.perfilcultivo?.parametros
    ?.find(p => p.sensor?.valores?.length > 0)
    ?.sensor?.valores?.slice(-1)?.[0]?.createdAt;
  const ultimatedTimeSensorDate = ultimatedTimeSensor ? new Date(ultimatedTimeSensor) : null;

  useEffect(() => {
    if (reports?.modulos?.length > 0 && !selectedModuleId) {
      const initialId = selectedModuleIdCard || reports.modulos[0]._id;
      setSelectedModuleIdChart(initialId);
    }
  }, [reports?.modulos, selectedModuleIdCard, selectedModuleId]);

  useEffect(() => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[1]);
    dispatch(getAllReports(id, startDate, endDate));

    return () => dispatch(cleanEstablishment());
  }, [dates]);

  useEffect(() => {
    return () => localStorage.removeItem('isLive');
  }, []);

  useEffect(() => {
    return () => dispatch(setSelectedModuleId(undefined));
  }, []);

  useEffect(() => {
    if (!isLive || !reports?.tiempo_actualizacion || !ultimatedTimeSensorDate) return;
    const now = new Date();
    const tiempoActualizacion = reports.tiempo_actualizacion * 60 * 1000;
    const elapsed = now.getTime() - ultimatedTimeSensorDate.getTime();
    const timeToNextFetch = tiempoActualizacion - (elapsed % tiempoActualizacion) + 60 * 1000;
    const timeout = setTimeout(() => {
      let startDate = new Date(now);
      switch (selectedRange) {
        case '1h':
          startDate.setHours(now.getHours() - 1);
          break;
        case '3h':
          startDate.setHours(now.getHours() - 3);
          break;
        case '12h':
          startDate.setHours(now.getHours() - 12);
          break;
        case '24h':
          startDate.setHours(now.getHours() - 24);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(dates[0]);
      }
      dispatch(getAllReports(id, startDate, now));
      const interval = setInterval(() => {
        const currentNow = new Date();
        let currentStart = new Date(currentNow);
        switch (selectedRange) {
          case '1h':
            currentStart.setHours(currentNow.getHours() - 1);
            break;
          case '3h':
            currentStart.setHours(currentNow.getHours() - 3);
            break;
          case '12h':
            currentStart.setHours(currentNow.getHours() - 12);
            break;
          case '24h':
            currentStart.setHours(currentNow.getHours() - 24);
            break;
          case 'week':
            currentStart.setDate(currentNow.getDate() - 7);
            break;
          case 'month':
            currentStart.setMonth(currentNow.getMonth() - 1);
            break;
          case 'quarter':
            currentStart.setMonth(currentNow.getMonth() - 3);
            break;
          case 'year':
            currentStart.setFullYear(currentNow.getFullYear() - 1);
            break;
          default:
            currentStart = new Date(dates[0]);
        }
        dispatch(getAllReports(id, currentStart, currentNow));
      }, tiempoActualizacion);
      return () => clearInterval(interval);
    }, timeToNextFetch);
    return () => clearTimeout(timeout);
  }, [isLive, reports?.tiempo_actualizacion, dispatch, id, ultimatedTimeSensorDate, selectedRange, dates]);

  const handleRangeChange = (rangeType) => {
    setSelectedRange(rangeType);
    const now = dayjs();
    let newRange = [now.subtract(7, 'day'), now];
    switch (rangeType) {
      case '1h':
        newRange = [now.subtract(1, 'hour'), now];
        break;
      case '3h':
        newRange = [now.subtract(3, 'hour'), now];
        break;
      case '12h':
        newRange = [now.subtract(12, 'hour'), now];
        break;
      case '24h':
        newRange = [now.subtract(24, 'hour'), now];
        break;
      case 'week':
        newRange = [now.subtract(7, 'day'), now];
        break;
      case 'month':
        newRange = [now.subtract(1, 'month'), now];
        break;
      case 'quarter':
        newRange = [now.subtract(3, 'month'), now];
        break;
      case 'year':
        newRange = [now.subtract(1, 'year'), now];
        break;
    }
    setDates(newRange);
  };

  const filteredModules = selectedModuleId
    ? reports?.modulos?.filter((mod) => mod._id === selectedModuleId)
    : [];

  const moduleOptions = reports?.modulos?.map((modulo) => ({
    label: `Módulo ${modulo.numero}`,
    value: modulo._id,
  }));

  const handleLiveChange = (value) => {
    setIsLive(value);
    localStorage.setItem('isLive', value);
  };

  const handleDownloadChart = () => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[1]);
    dispatch(getDowndloadReports(selectedModuleId, startDate, endDate));
  };

  useEffect(() => {
    if ( dataDownload instanceof Blob) {
      const blobUrl = window.URL.createObjectURL(dataDownload);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'reporte.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    }
  }, [dataDownload]);

  const selectedModule = reports?.modulos?.find(mod => mod._id === selectedModuleId);
  const columns = [
    {
      title: 'Rangos',
      dataIndex: 'rango',
      key: 'rango',
      fixed: 'left',
    },
    ...(selectedModule?.perfilcultivo?.parametros ?? []).map(({ sensor }) => ({
      title: sensor?.tipoSensor?.nombre || sensor?.nombre || 'Sensor',
      dataIndex: sensor?._id,
      key: sensor?._id,
    })),
  ];
  const data = React.useMemo(() => {
    if (!selectedModule || !selectedModule.perfilcultivo || !selectedModule.statistics) return [];
    const parametros = selectedModule.perfilcultivo.parametros;
    const statistics = selectedModule.statistics;
    const maxRow = { key: 'max', rango: 'Máximo' };
    const minRow = { key: 'min', rango: 'Mínimo' };
    const variationRow = { key: 'variation', rango: 'Variación' };
    const avgRow = { key: 'average', rango: 'Promedio' };
    parametros.forEach(({ sensor }) => {
      const sensorName = sensor.tipoSensor.nombre;
      const stats = statistics[sensorName];
      if (stats) {
        maxRow[sensor._id] = stats.maximo ?? '-';
        minRow[sensor._id] = stats.minimo ?? '-';
        const variation = parseFloat(stats.variacion);
        const promedio = parseFloat(stats.promedio);
        variationRow[sensor._id] = !isNaN(variation) ? variation.toFixed(2) : '-';
        avgRow[sensor._id] = !isNaN(promedio) ? promedio.toFixed(2) : '-';
      } else {
        maxRow[sensor._id] = '-';
        minRow[sensor._id] = '-';
        variationRow[sensor._id] = '-';
        avgRow[sensor._id] = '-';
      }
    });
    return [maxRow, minRow, variationRow, avgRow];
  }, [selectedModule]);

const isDataReady = !isLoading && reports?.modulos?.length > 0 && selectedModule;

  return (
    !isDataReady ? <Spinner/>:
    <div className="container-page">
      <div className="container-page__header">
        <div className="reports-header">
          <div>
          <Title text="Reportes" />
          </div>
          <div>
          <Select
            options={moduleOptions}
            value={selectedModuleId}
            onChange={(value) => setSelectedModuleIdChart(value)}
            placeholder="Seleccionar módulo"
          />
          </div>
          <div className="reports-filters">
          <div className='reports-header__options'>
          <div>
          <RangePicker
  value={dates}
  onChange={(value) => {
    setDates(value);
    setSelectedRange(null);
  }}
  format={dateFormat}
  showTime={{ format: 'HH:mm:ss' }}
  allowClear={false}
/>
        </div>
        <div>
            <div>
              <span>Ver por:</span>
            </div>
            <div className='reports-header__filters'>
              <div className='reports-header__options-days'>
              {rangeOptions.map((range) => (
                <Button
                  key={range.value}
                  className={`range-button ${selectedRange === range.value ? 'active' : ''}`}
                  onClick={() => handleRangeChange(range.value)}
                >
            {range.label}
                </Button>
))}
              </div>
        </div>
            </div>
        </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="container-page__modules">
        {filteredModules.map((modulo) => {
  return(<div key={modulo._id}>
    {modulo.perfilcultivo ? (
      <>
        <ChartReport data={modulo} onLiveChange={handleLiveChange} onDownloadChart={handleDownloadChart} />
      </>
    ) : (
      <p className="container-page__subtitle">
        {`El módulo ${modulo.numero} no tiene perfiles de cultivos asociados`}
      </p>
    )}
  </div>);
})}
</div>
      )}
{!isDataReady ?  <Spinner/> :
    <Table
  columns={columns}
  dataSource={data}
  pagination={false}
/>}
    </div>
  );
};
export default withAuth(Reports);
